import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import styles from "./ResponsiveSettingsTabs.module.css";

const MORE_BUTTON_SPACE = 118;
const TAB_GAP = 8;

export default function ResponsiveSettingsTabs({
  tabs,
  activeKey,
  onChange,
  ariaLabel,
  dataAcademyPrefix,
}) {
  const { t } = useTranslation();
  const rootRef = useRef(null);
  const measureRef = useRef(null);
  const menuRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(tabs.length);
  const [open, setOpen] = useState(false);

  const recalculate = useCallback(() => {
    const root = rootRef.current;
    const measure = measureRef.current;
    if (!root || !measure) return;

    const widths = Array.from(measure.children).map((node) =>
      Math.ceil(node.getBoundingClientRect().width)
    );
    const available = Math.floor(root.getBoundingClientRect().width);
    const fullWidth = widths.reduce(
      (total, width, index) => total + width + (index ? TAB_GAP : 0),
      0
    );

    if (fullWidth <= available) {
      setVisibleCount(tabs.length);
      return;
    }

    const limit = Math.max(120, available - MORE_BUTTON_SPACE);
    let used = 0;
    let count = 0;
    widths.forEach((width) => {
      const next = used + width + (count ? TAB_GAP : 0);
      if (next <= limit) {
        used = next;
        count += 1;
      }
    });
    setVisibleCount(Math.max(1, Math.min(count, tabs.length - 1)));
  }, [tabs]);

  useLayoutEffect(() => {
    recalculate();
  }, [recalculate]);

  useEffect(() => {
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(recalculate);
    if (rootRef.current) observer?.observe(rootRef.current);
    window.addEventListener("resize", recalculate);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", recalculate);
    };
  }, [recalculate]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (event.key === "Escape" || !menuRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [open]);

  const visibleTabs = tabs.slice(0, visibleCount);
  const hiddenTabs = tabs.slice(visibleCount);
  const activeHiddenTab = useMemo(
    () => hiddenTabs.find((tab) => tab.key === activeKey),
    [activeKey, hiddenTabs]
  );

  const select = (key) => {
    onChange(key);
    setOpen(false);
  };

  return (
    <div className={styles.shell} ref={rootRef}>
      <div className={styles.measure} ref={measureRef} aria-hidden="true">
        {tabs.map((tab) => (
          <span key={tab.key} className={styles.measureTab}>
            {tab.label}
          </span>
        ))}
      </div>

      <div className={styles.row} role="tablist" aria-label={ariaLabel}>
        {visibleTabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeKey === tab.key}
            data-academy-tab={
              dataAcademyPrefix ? `${dataAcademyPrefix}-${tab.key}` : undefined
            }
            className={`${styles.tab} ${
              activeKey === tab.key ? styles.active : ""
            }`}
            onClick={() => select(tab.key)}
          >
            {tab.label}
          </button>
        ))}

        {hiddenTabs.length > 0 ? (
          <div className={styles.moreWrap} ref={menuRef}>
            <button
              type="button"
              className={`${styles.moreButton} ${
                activeHiddenTab ? styles.active : ""
              }`}
              aria-expanded={open}
              aria-haspopup="menu"
              onClick={() => setOpen((current) => !current)}
            >
              <span>
                {activeHiddenTab?.label ||
                  t("settings_tabs_more", { defaultValue: "Ещё" })}
              </span>
              <span className={styles.chevron} aria-hidden="true">
                {open ? "⌃" : "⌄"}
              </span>
            </button>

            {open ? (
              <div className={styles.menu} role="menu">
                <div className={styles.menuTitle}>
                  {t("settings_tabs_all", { defaultValue: "Другие разделы" })}
                </div>
                {hiddenTabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    role="menuitem"
                    className={activeKey === tab.key ? styles.menuActive : ""}
                    onClick={() => select(tab.key)}
                  >
                    <span>{tab.label}</span>
                    {activeKey === tab.key ? <i className="lni lni-check" /> : null}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
